/**
 * CORE-E — the canonical authority behind one category, for explanation.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS, AND WHY IT IS NOT THE HEATMAP'S EXISTING HOOK
 * ═══════════════════════════════════════════════════════════════════════════
 * `useSkillHeatmapData` reads `technician_proficiencies` (the portable Passport
 * snapshot) and `technicians.proficiencies` (the shop map). Neither is the
 * authority any more. Since `20261122120000` the value scheduling actually
 * consumes is recomputed into `technician_category_authority`, and the durable
 * record of what moved it lives in `technician_proficiency_evidence`.
 *
 * Explaining a score from a mirror of it would be the same defect this whole
 * campaign exists to find: a surface describing a number nobody is deciding
 * with. So this hook reads the authority itself, and the heatmap's category
 * detail explains THAT.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * IT IS SHOP-SCOPED, BECAUSE THE AUTHORITY IS
 * ═══════════════════════════════════════════════════════════════════════════
 * `technician_category_authority` is keyed by (shop, technician, category) —
 * the scheduling value belongs to a shop's roster row, not to the person in the
 * abstract. A Passport viewed with no live shop attachment therefore has no
 * scheduling authority to describe, and this returns nothing rather than
 * inventing one from the portable snapshot.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * RELEASE ORDER: MIGRATION FIRST, THIS FILE SECOND
 * ═══════════════════════════════════════════════════════════════════════════
 * This hook SELECTS `baseline_prior_strength`, added by CORE-E Stage A
 * (`20261228120000_core_e_prior_carries_its_own_strength.sql`).
 *
 * PostgREST does not ignore a column it does not know. Selecting one that is
 * absent from the deployed schema fails the ENTIRE request with 42703, and the
 * `authorityError` throw below turns that into a broken Passport category
 * detail — not a degraded one. The `?? BASELINE_PRIOR_STRENGTH` default further
 * down is a VALUE fallback for a null column, and cannot rescue a missing one.
 *
 * So the dependency is a hard ordering, not a preference:
 *
 *   the Stage A migration MUST be applied to a database BEFORE any frontend
 *   bundle containing this select becomes active against it.
 *
 * No compatibility query is attempted deliberately. `useAutoBump` does guard
 * individual columns against 42703, but that is one scheduling hot path that
 * must survive a partially-migrated production; it is not a repo-wide
 * convention, and adding that machinery here would buy nothing the controlled
 * release order already guarantees while leaving a second, rarely-exercised
 * code path to rot.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** What CORE-E knows about a technician's opening prior. */
export type BaselineClass =
  | 'MANAGER_ASSERTED'
  | 'PASSPORT_CARRIED'
  | 'LEGACY_UNCLASSIFIED';

export interface CategoryAuthorityRow {
  categoryKey: string;
  /** The value scheduling consumes. Null before anything has been computed. */
  score: number | null;
  /** The opening prior, and what is known about where it came from. */
  baselineScore: number | null;
  baselineClass: BaselineClass | null;
  /**
   * CORE-E Stage A: how much credible background stands behind the prior,
   * expressed as the number of observations it is worth. NOT a proficiency —
   * a larger value makes nobody more skilled, only their opening estimate
   * slower to be outweighed. 8 for every row until Stage B derives it.
   */
  baselinePriorStrength: number;
  /** How much demonstrated work stands behind the score. */
  evidenceCount: number;
  confidence: number;
  /** Never positive. The total attributable-defect movement applied. */
  defectAdjustment: number;
  updatedAt: string | null;
}

export interface CategoryEvidenceRow {
  id: string;
  kind: 'CLEAN_MATURED' | 'ATTRIBUTED_DEFECT' | 'UNATTRIBUTED_DEFECT';
  ticketId: string;
  outcomeScore: number | null;
  appliedDelta: number | null;
  scoreBefore: number | null;
  scoreAfter: number | null;
  maturedAt: string | null;
  reversedAt: string | null;
  reversedByTicketId: string | null;
  sourceCompletedAt: string | null;
}

export interface CategoryAuthority {
  authority: CategoryAuthorityRow | null;
  evidence: CategoryEvidenceRow[];
}

/**
 * The DEFAULT prior strength, mirrored from `core_e_baseline_prior_strength()`.
 *
 * Used for ONE statement — whether demonstrated work now outweighs the opening
 * estimate — which is the crossing point, not the blend. The weighting formula
 * itself stays where it belongs, in the authority.
 */
export const BASELINE_PRIOR_STRENGTH = 8;

/**
 * True once demonstrated evidence carries more weight than the opening prior.
 *
 * CORE-E Stage A: the crossing point is a property of the ROW, because a prior
 * now carries its own strength. Passing the row's `baselinePriorStrength` keeps
 * this sentence true for a veteran whose prior is worth thirty observations
 * rather than eight; omitting it falls back to the global default, which is
 * what every row holds until Stage B derives otherwise. Hard-coding 8 here
 * would have made the Passport quietly wrong the day Stage B landed.
 */
export function evidenceHasSupersededPrior(
  evidenceCount: number,
  priorStrength: number = BASELINE_PRIOR_STRENGTH,
): boolean {
  const strength =
    Number.isFinite(priorStrength) && priorStrength > 0
      ? priorStrength
      : BASELINE_PRIOR_STRENGTH;
  return evidenceCount > strength;
}

export function useCategoryAuthority(
  shopTechnicianId: string | null | undefined,
  categoryKeys: string[],
) {
  return useQuery<Record<string, CategoryAuthority>>({
    queryKey: ['core-e-category-authority', shopTechnicianId, categoryKeys.join(',')],
    enabled: !!shopTechnicianId && categoryKeys.length > 0,
    queryFn: async () => {
      const byCategory: Record<string, CategoryAuthority> = {};

      // `technician_category_authority` and `technician_proficiency_evidence`
      // come from `20261122120000`, which is NOT APPLIED to the environment the
      // Supabase types are generated from — so the generated union does not
      // name them and the typed client rejects both, one of them with a
      // recursion blow-up rather than a readable message.
      //
      // `(supabase as any)` is this codebase's established shape for exactly
      // that situation (see `src/hooks/cannedJobs/*`). The row handling below
      // is already written against `Record<string, unknown>` with explicit
      // per-field coercion, so nothing here was relying on the generated types
      // for safety in the first place.
      //
      // This is the one place to revisit when the migration lands: dropping the
      // cast should then typecheck unchanged.
      const { data: authorityRows, error: authorityError } = await (supabase as any)
        .from('technician_category_authority')
        .select(
          'category_key, score, baseline_score, baseline_class, baseline_prior_strength, evidence_count, confidence, defect_adjustment, updated_at',
        )
        .eq('technician_id', shopTechnicianId!)
        .in('category_key', categoryKeys);
      if (authorityError) throw authorityError;

      for (const row of (authorityRows ?? []) as Array<Record<string, unknown>>) {
        byCategory[String(row.category_key)] = {
          authority: {
            categoryKey: String(row.category_key),
            score: row.score === null ? null : Number(row.score),
            baselineScore: row.baseline_score === null ? null : Number(row.baseline_score),
            baselineClass: (row.baseline_class as BaselineClass | null) ?? null,
            // A VALUE fallback, not a schema fallback. It covers a column that
            // exists and came back null or undefined; it does NOT cover a
            // database without the column, because PostgREST rejects the whole
            // request when a selected column is unknown (42703) and this
            // function throws on `authorityError` above. See the release-order
            // note at the top of this file.
            baselinePriorStrength: Number(row.baseline_prior_strength ?? BASELINE_PRIOR_STRENGTH),
            evidenceCount: Number(row.evidence_count ?? 0),
            confidence: Number(row.confidence ?? 0),
            defectAdjustment: Number(row.defect_adjustment ?? 0),
            updatedAt: (row.updated_at as string | null) ?? null,
          },
          evidence: [],
        };
      }

      const { data: evidenceRows, error: evidenceError } = await (supabase as any)
        .from('technician_proficiency_evidence')
        .select(
          'id, category_key, kind, ticket_id, outcome_score, applied_delta, score_before, score_after, matured_at, reversed_at, reversed_by_ticket_id, source_completed_at',
        )
        .eq('technician_id', shopTechnicianId!)
        .in('category_key', categoryKeys)
        .order('source_completed_at', { ascending: false });
      if (evidenceError) throw evidenceError;

      for (const row of (evidenceRows ?? []) as Array<Record<string, unknown>>) {
        const key = String(row.category_key);
        if (!byCategory[key]) byCategory[key] = { authority: null, evidence: [] };
        byCategory[key].evidence.push({
          id: String(row.id),
          kind: row.kind as CategoryEvidenceRow['kind'],
          ticketId: String(row.ticket_id),
          outcomeScore: row.outcome_score === null ? null : Number(row.outcome_score),
          appliedDelta: row.applied_delta === null ? null : Number(row.applied_delta),
          scoreBefore: row.score_before === null ? null : Number(row.score_before),
          scoreAfter: row.score_after === null ? null : Number(row.score_after),
          maturedAt: (row.matured_at as string | null) ?? null,
          reversedAt: (row.reversed_at as string | null) ?? null,
          reversedByTicketId: (row.reversed_by_ticket_id as string | null) ?? null,
          sourceCompletedAt: (row.source_completed_at as string | null) ?? null,
        });
      }

      return byCategory;
    },
  });
}
